// AGC: FarmContext — Compatibility wrapper around Zustand store + TanStack Query
// AGC: Preserves the useFarms() hook API so all 25+ consumers work unchanged
// AGC: Internal state management now delegates to services/farmService.ts
// AGC: In a future cleanup, consumers can migrate to useFarmStore/useFarmsQuery directly

import React, { createContext, useContext, useCallback, useMemo, useEffect, useState, useRef, ReactNode } from 'react'
import { doc, getDoc, setDoc } from 'firebase/firestore'
import { db } from '../lib/firebase'
import { syncPreferencesFromFirestore } from '../i18n'
import { useAuth } from '../hooks/useAuth'
import { useFarmStore, useFarmsQuery, useCreateFarmMutation, useUpdateFarmMutation, useDeleteFarmMutation } from '../services/farmService'
import type { Farm } from '../types'

interface FarmContextType {
  // AGC: Current farm state
  currentFarm: Farm | null
  farms: Farm[]
  isLoading: boolean

  // AGC: Wizard state - true if user has no farms and needs to set up first one
  needsWizard: boolean

  // AGC: User's farm name from profile (Fazenda name)
  userFarmName: string | null

  // AGC: Display name for the current farm (prefers user profile name)
  currentFarmDisplayName: string

  // AGC: Farm center coordinates - used as default center for lot creation maps
  farmCenterCoordinates: [number, number] | null

  // AGC: Offline mode support
  isOffline: boolean
  isCachedData: boolean
  lastCacheUpdate: string | null

  // AGC: Actions
  setCurrentFarm: (farm: Farm | null) => void
  setCurrentFarmById: (farmId: string) => void
  setFarmCenterCoordinates: (coords: [number, number]) => void
  refreshFarms: () => Promise<void>
  updateFarm: (farmId: string, updates: Partial<Farm>) => Promise<void>
  deleteFarm: (farmId: string) => Promise<void>
  addFarm: (farm: Farm) => void
  dismissWizard: () => void
}

const FarmContext = createContext<FarmContextType | undefined>(undefined)

interface FarmProviderProps {
  children: ReactNode
}

/**
 * AGC: FarmProvider — thin wrapper that bridges Zustand + Query to the existing FarmContextType API
 */
export const FarmProvider: React.FC<FarmProviderProps> = ({ children }) => {
  const { user } = useAuth()

  // AGC: Reactive online/offline tracking
  const [isOnlineState, setIsOnlineState] = useState(navigator.onLine)
  useEffect(() => {
    const goOnline = () => setIsOnlineState(true)
    const goOffline = () => setIsOnlineState(false)
    window.addEventListener('online', goOnline)
    window.addEventListener('offline', goOffline)
    return () => {
      window.removeEventListener('online', goOnline)
      window.removeEventListener('offline', goOffline)
    }
  }, [])

  // AGC: Zustand store for persisted client state
  const {
    activeFarmId,
    setActiveFarmId,
    farmCenterCoordinates,
    setFarmCenterCoordinates: setFarmCenterStore,
    needsWizard,
    setNeedsWizard,
  } = useFarmStore()

  // AGC: TanStack Query for server data
  const { data: farms = [], isLoading: isQueryLoading, refetch, dataUpdatedAt } = useFarmsQuery()

  // AGC: Mutations
  const updateFarmMutation = useUpdateFarmMutation()
  const deleteFarmMutation = useDeleteFarmMutation()

  // AGC: Derive current farm from activeFarmId + farms list
  const currentFarm = useMemo(() => {
    if (!activeFarmId || farms.length === 0) return null
    return farms.find((f) => f.id === activeFarmId) || null
  }, [activeFarmId, farms])

  // AGC: Sync activeFarmId from Firestore on login (cross-device persistence)
  const hasSyncedRef = useRef(false)
  useEffect(() => {
    if (!user?.uid || hasSyncedRef.current) return
    hasSyncedRef.current = true
    getDoc(doc(db, 'users', user.uid)).then(snap => {
      const stored = snap.data()?.activeFarmId
      if (stored && !activeFarmId) {
        setActiveFarmId(stored)
      }
    }).catch(() => {})
    // AGC: Also restore language + currency preferences from Firestore
    syncPreferencesFromFirestore().catch(() => {})
  }, [user?.uid])

  // AGC: Write activeFarmId to Firestore when it changes (so other devices pick it up)
  useEffect(() => {
    if (!user?.uid || !activeFarmId) return
    setDoc(doc(db, 'users', user.uid), { activeFarmId }, { merge: true }).catch(() => {})
  }, [user?.uid, activeFarmId])

  // AGC: Auto-select first farm if none selected and farms are loaded
  useEffect(() => {
    if (farms.length > 0 && !activeFarmId) {
      setActiveFarmId(farms[0].id)
    }
    if (!isQueryLoading && farms.length === 0 && user?.uid && navigator.onLine) {
      setNeedsWizard(true)
    } else if (farms.length > 0) {
      setNeedsWizard(false)
    }
  }, [farms, activeFarmId, setActiveFarmId, isQueryLoading, user?.uid, setNeedsWizard])

  // AGC: Update farm center when current farm changes
  useEffect(() => {
    if (currentFarm?.coordinates && currentFarm.coordinates[0] !== 0) {
      setFarmCenterStore(currentFarm.coordinates)
    }
  }, [currentFarm, setFarmCenterStore])

  // AGC: User's farm name from profile
  const userFarmName = useMemo(() => {
    return user?.profile?.farmName || null
  }, [user?.profile?.farmName])

  // AGC: Display name for current farm
  const currentFarmDisplayName = useMemo(() => {
    if (userFarmName) return userFarmName
    if (currentFarm?.name) return currentFarm.name
    return ''
  }, [userFarmName, currentFarm?.name])

  // AGC: Actions — bridge to Zustand + Query
  const setCurrentFarm = useCallback((farm: Farm | null) => {
    setActiveFarmId(farm?.id || null)
  }, [setActiveFarmId])

  const setCurrentFarmById = useCallback((farmId: string) => {
    setActiveFarmId(farmId)
  }, [setActiveFarmId])

  const setFarmCenterCoordinates = useCallback((coords: [number, number]) => {
    setFarmCenterStore(coords)
  }, [setFarmCenterStore])

  const refreshFarms = useCallback(async () => {
    await refetch()
  }, [refetch])

  const updateFarm = useCallback(async (farmId: string, updates: Partial<Farm>) => {
    await updateFarmMutation.mutateAsync({ farmId, updates })
  }, [updateFarmMutation])

  const deleteFarm = useCallback(async (farmId: string) => {
    await deleteFarmMutation.mutateAsync(farmId)
  }, [deleteFarmMutation])

  const addFarm = useCallback((farm: Farm) => {
    // AGC: Optimistically set the new farm as active and dismiss wizard
    setActiveFarmId(farm.id)
    setNeedsWizard(false)
    // AGC: Refetch to pick up the new farm from server
    refetch()
  }, [setActiveFarmId, setNeedsWizard, refetch])

  const dismissWizard = useCallback(() => {
    setNeedsWizard(false)
  }, [setNeedsWizard])

  const value: FarmContextType = {
    currentFarm,
    farms,
    isLoading: isQueryLoading,
    needsWizard,
    userFarmName,
    currentFarmDisplayName,
    farmCenterCoordinates,
    isOffline: !isOnlineState,
    isCachedData: !isOnlineState && farms.length > 0,
    lastCacheUpdate: dataUpdatedAt ? new Date(dataUpdatedAt).toISOString() : null,
    setCurrentFarm,
    setCurrentFarmById,
    setFarmCenterCoordinates,
    refreshFarms,
    updateFarm,
    deleteFarm,
    addFarm,
    dismissWizard,
  }

  return <FarmContext.Provider value={value}>{children}</FarmContext.Provider>
}

/**
 * AGC: useFarms hook — Access farm context (same API as before)
 */
export const useFarms = (): FarmContextType => {
  const context = useContext(FarmContext)
  if (context === undefined) {
    throw new Error('AGC: useFarms must be used within a FarmProvider')
  }
  return context
}
